/**
 * serverVersionsCache -- Module-wide, de-duplicated loader for media-server web-UI
 * versions, so the deep-link builders can pick the right route form without every
 * page mount re-pinging all servers.
 *
 * `useServerVersions` (in useServerProxy) wraps the single shared loader created
 * here. A fresh cached result (inside the TTL) serves with no fetch; concurrent
 * callers share one in-flight request; a failed fetch is NOT cached, so the next
 * caller retries. `useServerPing` stays live and uncached for the settings/health
 * views that surface per-request latency.
 */

export interface ServerVersionInfo {
  serverType: string;
  version: string | null;
  ok: boolean;
}

export interface ServerVersionsLoader {
  /** Resolve the versions, serving a fresh cache or sharing an in-flight fetch. */
  load(): Promise<ServerVersionInfo[]>;
  /** The fresh cached snapshot, or null when absent/expired (no fetch). */
  peek(): ServerVersionInfo[] | null;
}

export const SERVER_VERSIONS_TTL_MS = 10 * 60 * 1000;

/**
 * Build a loader around a fetch function. The cache, in-flight dedup, and TTL are
 * closed over, so each loader is independent — the test creates its own with a spy
 * fetch and a controllable clock.
 */
export function createServerVersionsLoader(
  fetchVersions: () => Promise<ServerVersionInfo[]>,
  opts: { ttlMs?: number; now?: () => number } = {},
): ServerVersionsLoader {
  const ttlMs = opts.ttlMs ?? SERVER_VERSIONS_TTL_MS;
  const now = opts.now ?? Date.now;

  let cache: { at: number; data: ServerVersionInfo[] } | null = null;
  let inFlight: Promise<ServerVersionInfo[]> | null = null;

  const isFresh = () => cache != null && now() - cache.at < ttlMs;

  return {
    peek() {
      return isFresh() && cache ? cache.data : null;
    },
    load() {
      if (isFresh() && cache) return Promise.resolve(cache.data);
      if (inFlight) return inFlight;
      inFlight = fetchVersions().then(
        (data) => {
          cache = { at: now(), data };
          inFlight = null;
          return data;
        },
        (err) => {
          // A failed fetch is not cached — the next caller retries.
          inFlight = null;
          throw err;
        },
      );
      return inFlight;
    },
  };
}
